import { SearchedApPayee } from 'ap-openapi';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const usePayeeListHandler = (payees: SearchedApPayee[]) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasMultipleRows, setHasMultipleRows] = useState(false);
  const [visibleIndices, setVisibleIndices] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateVisibleTags = useCallback(() => {
    const container = containerRef.current;
    if (!container || payees.length === 0) return;
    // Get all tags as children of the container
    const tags = Array.from(container.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement
    );
    if (tags.length === 0) return;

    const firstTag = tags[0];
    if (!firstTag) return;
    const firstTagRect = firstTag.getBoundingClientRect();

    // Find tags in the first row (same Y position as first tag)
    const firstRowTags = tags.reduce<number[]>((acc, tag, index) => {
      const tagRect = tag.getBoundingClientRect();
      const isSameRow = Math.abs(tagRect.top - firstTagRect.top) <= 1;
      return isSameRow ? [...acc, index] : acc;
    }, []);

    // Update state
    const hasMultiple = firstRowTags.length < tags.length;
    setHasMultipleRows(hasMultiple);
    setVisibleIndices(
      hasMultiple && !isExpanded ? firstRowTags : tags.map((_, i) => i)
    );
  }, [payees, isExpanded]);

  useLayoutEffect(() => {
    calculateVisibleTags();
    // Handle resize events
    const observer = new ResizeObserver(() =>
      requestAnimationFrame(calculateVisibleTags)
    );
    const container = containerRef.current;

    if (container) {
      observer.observe(container);
      container.parentElement && observer.observe(container.parentElement);
    }

    window.addEventListener('resize', calculateVisibleTags);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', calculateVisibleTags);
    };
  }, [calculateVisibleTags]);

  return {
    isExpanded,
    hasMultipleRows,
    visibleIndices,
    containerRef,
    toggleExpand: () => setIsExpanded((prev) => !prev),
  };
};
